import { getEffectiveConnectionMode } from './databaseConnectionMode'
import { DATABASE_UI } from '../constants/databaseUIMessages'
import {
  type DatabaseConnectionView,
  databaseConnectionPath,
  databaseConnectionViewSegment,
  isDatabaseConnectionPath,
} from './databaseConnectionNav'
import {
  type DatabaseSchemaView,
  databaseSchemaPath,
  databaseSchemaViewSegment,
  isDatabaseSchemaPath,
} from './databaseSchemaNav'

export type DatabaseDetailTab =
  | 'diagram'
  | 'detailed_rules'
  | 'preview_queries'
  | 'query_history'
  | 'pending_writes'
  | 'forks'
  | 'connection'
  | 'keys'
  | 'sensitive_columns'

export const DATABASE_DETAIL_TAB_SEGMENTS: Record<DatabaseDetailTab, string> = {
  sensitive_columns: 'sensitive-columns',
  diagram: 'diagram',
  detailed_rules: 'detailed-rules',
  preview_queries: 'preview-queries',
  query_history: 'query-history',
  pending_writes: 'pending-writes',
  forks: 'forks',
  connection: 'connection',
  keys: 'keys',
}

export const DATABASE_DETAIL_TAB_ROUTE_SEGMENTS = (
  Object.keys(DATABASE_DETAIL_TAB_SEGMENTS) as DatabaseDetailTab[]
)
  .filter((tab) => tab !== 'diagram' && tab !== 'connection')
  .map((tab) => DATABASE_DETAIL_TAB_SEGMENTS[tab])

export type DatabaseNavFlags = {
  dataClassificationEnabled: boolean
  orgSettingsLoading: boolean
  approvedWritesEnabled: boolean
  selfHosted: boolean
}

export type DatabaseNavItemKind =
  | DatabaseDetailTab
  | 'general'
  | 'classification'
  | 'snapshots'
  | 'statistics'
  | 'users'
  | 'schema'

export function databaseUsersPath(databaseId: string | number): string {
  return `/databases/${databaseId}/users`
}

export type DatabaseSchemaNavSubItem = {
  view: DatabaseSchemaView
  label: string
  path: string
}

export type DatabaseConnectionNavSubItem = {
  view: DatabaseConnectionView
  label: string
  path: string
}

export function databaseGeneralSettingsPath(databaseId: string | number): string {
  return `/databases/${databaseId}/general-settings`
}

export type DatabaseNavItem = {
  kind: DatabaseNavItemKind
  label: string
  path: string
  badge?: number | null
}

export function defaultDatabaseDetailTab(flags: Pick<DatabaseNavFlags, 'dataClassificationEnabled'>): DatabaseDetailTab {
  return flags.dataClassificationEnabled ? 'sensitive_columns' : 'detailed_rules'
}

export function databaseDetailTabPath(
  databaseId: string,
  tab: DatabaseDetailTab,
  dataClassificationEnabled: boolean,
): string {
  if (tab === 'connection') return databaseConnectionPath(databaseId, 'upstream')
  const defaultTab = defaultDatabaseDetailTab({ dataClassificationEnabled })
  if (tab === defaultTab) return `/databases/${databaseId}`
  return `/databases/${databaseId}/${DATABASE_DETAIL_TAB_SEGMENTS[tab]}`
}

export function databaseDetailPathSegment(pathname: string, databaseId: string): string | null {
  const prefix = `/databases/${databaseId}/`
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length).replace(/\/$/, '')
  if (!rest || rest.includes('/')) return null
  return rest
}

function normalizeTabToken(raw: string): string {
  const t = raw.trim().toLowerCase()
  if (t === 'detailed_rules') return 'detailed-rules'
  if (t === 'preview_queries') return 'preview-queries'
  if (t === 'query_history') return 'query-history'
  if (t === 'pending_writes' || t === 'write_requests') return 'pending-writes'
  if (t === 'sensitive_columns') return 'sensitive-columns'
  return t
}

export function parseDatabaseDetailTabParam(
  mode: ReturnType<typeof getEffectiveConnectionMode>,
  raw: string | null,
  flags: DatabaseNavFlags,
): DatabaseDetailTab {
  const { dataClassificationEnabled, orgSettingsLoading, approvedWritesEnabled } = flags
  const t = normalizeTabToken(typeof raw === 'string' ? raw : '')
  if (!t || t === 'overview' || t === 'activity') {
    return defaultDatabaseDetailTab({ dataClassificationEnabled })
  }
  if (t === 'connection') return 'connection'
  if (t === 'diagram') return 'detailed_rules'
  if (t === 'detailed-rules') return 'detailed_rules'
  if (t === 'preview-queries') return 'preview_queries'
  if (t === 'query-history') return 'query_history'
  if (t === 'pending-writes') {
    if (approvedWritesEnabled || orgSettingsLoading) return 'pending_writes'
  }
  if (t === 'forks') return 'forks'
  if (t === 'keys') return mode === 'self_hosted' ? 'keys' : defaultDatabaseDetailTab({ dataClassificationEnabled })
  if (t === 'sensitive-columns') {
    if (dataClassificationEnabled || orgSettingsLoading) return 'sensitive_columns'
  }
  return defaultDatabaseDetailTab({ dataClassificationEnabled })
}

export function parseDatabaseDetailPathSegment(
  segment: string | null | undefined,
  mode: ReturnType<typeof getEffectiveConnectionMode>,
  flags: DatabaseNavFlags,
): DatabaseDetailTab {
  if (!segment) return defaultDatabaseDetailTab(flags)
  return parseDatabaseDetailTabParam(mode, segment, flags)
}

export function databaseDetailPathNeedsRewrite(
  databaseId: string,
  pathname: string,
  resolvedTab: DatabaseDetailTab,
  dataClassificationEnabled: boolean,
): string | null {
  const want = databaseDetailTabPath(databaseId, resolvedTab, dataClassificationEnabled)
  const current = pathname.replace(/\/$/, '')
  const wantPath = want.replace(/\/$/, '')
  return current === wantPath ? null : want
}

/** @deprecated Legacy `?tab=` URLs — prefer `databaseDetailTabPath`. */
export function tabToSearchParamValue(
  tab: DatabaseDetailTab,
  dataClassificationEnabled: boolean,
): string | undefined {
  const path = databaseDetailTabPath('0', tab, dataClassificationEnabled)
  const segment = path.split('/').pop()
  if (segment === '0') return undefined
  return segment === undefined ? undefined : segment
}

export function getDatabaseNavItems(
  databaseId: string,
  flags: DatabaseNavFlags,
  pendingWritesCount: number | null = null,
): DatabaseNavItem[] {
  const items: DatabaseNavItem[] = []
  const { dataClassificationEnabled, orgSettingsLoading, approvedWritesEnabled } = flags

  items.push({
    kind: 'general',
    label: 'General',
    path: databaseGeneralSettingsPath(databaseId),
  })

  items.push({
    kind: 'users',
    label: 'Users',
    path: databaseUsersPath(databaseId),
  })

  if (dataClassificationEnabled || orgSettingsLoading) {
    items.push({
      kind: 'sensitive_columns',
      label: 'Sensitive data',
      path: databaseDetailTabPath(databaseId, 'sensitive_columns', dataClassificationEnabled),
    })
  }

  items.push({
    kind: 'detailed_rules',
    label: DATABASE_UI.tab_detailedRules,
    path: `/databases/${databaseId}/${DATABASE_DETAIL_TAB_SEGMENTS.detailed_rules}`,
  })
  items.push({
    kind: 'preview_queries',
    label: 'Preview queries',
    path: databaseDetailTabPath(databaseId, 'preview_queries', dataClassificationEnabled),
  })

  items.push({
    kind: 'snapshots',
    label: 'Snapshots',
    path: `/databases/${databaseId}/snapshots`,
  })

  items.push({
    kind: 'statistics',
    label: 'Statistics',
    path: `/databases/${databaseId}/statistics`,
  })

  items.push({
    kind: 'forks',
    label: 'Forks',
    path: databaseDetailTabPath(databaseId, 'forks', dataClassificationEnabled),
  })

  if (approvedWritesEnabled || orgSettingsLoading) {
    items.push({
      kind: 'pending_writes',
      label: 'Write requests',
      path: databaseDetailTabPath(databaseId, 'pending_writes', dataClassificationEnabled),
      badge: orgSettingsLoading ? null : pendingWritesCount,
    })
  }

  items.push({
    kind: 'connection',
    label: 'Connection',
    path: databaseConnectionPath(databaseId, 'upstream'),
  })

  if (flags.selfHosted) {
    items.push({
      kind: 'keys',
      label: 'API keys',
      path: databaseDetailTabPath(databaseId, 'keys', dataClassificationEnabled),
    })
  }

  if (dataClassificationEnabled) {
    items.push({
      kind: 'classification',
      label: 'Classification suggestions',
      path: `/databases/${databaseId}/classification-suggestions`,
    })
  }

  items.push({
    kind: 'query_history',
    label: 'Query history',
    path: databaseDetailTabPath(databaseId, 'query_history', dataClassificationEnabled),
  })

  return items
}

export function getDatabaseSchemaNavSubItems(databaseId: string): DatabaseSchemaNavSubItem[] {
  return [
    { view: 'diagram', label: 'Diagram', path: databaseSchemaPath(databaseId, 'diagram') },
    { view: 'table', label: 'Table', path: databaseSchemaPath(databaseId, 'table') },
  ]
}

export function getDatabaseConnectionNavSubItems(databaseId: string): DatabaseConnectionNavSubItem[] {
  return [
    { view: 'upstream', label: 'Upstream', path: databaseConnectionPath(databaseId, 'upstream') },
    { view: 'downstream', label: 'Downstream', path: databaseConnectionPath(databaseId, 'downstream') },
    { view: 'parameters', label: 'Parameters', path: databaseConnectionPath(databaseId, 'parameters') },
  ]
}

/** Active only on `/schema` root before redirect; sub-views highlight their sub-item instead. */
export function isDatabaseSchemaNavItemActive(
  pathname: string,
  databaseId: string,
): boolean {
  if (!isDatabaseSchemaPath(pathname, databaseId)) return false
  return databaseSchemaViewSegment(pathname, databaseId) === null
}

export function databaseSchemaNavItemPath(databaseId: string): string {
  return databaseSchemaPath(databaseId, 'diagram')
}

export function isDatabaseSchemaSubItemActive(
  pathname: string,
  databaseId: string,
  view: DatabaseSchemaView,
): boolean {
  if (!isDatabaseSchemaPath(pathname, databaseId)) return false
  return databaseSchemaViewSegment(pathname, databaseId) === view
}

/** Active only on `/connection` root before redirect; sub-views highlight their sub-item instead. */
export function isDatabaseConnectionNavItemActive(
  pathname: string,
  databaseId: string,
): boolean {
  if (!isDatabaseConnectionPath(pathname, databaseId)) return false
  return databaseConnectionViewSegment(pathname, databaseId) === null
}

export function databaseConnectionNavItemPath(databaseId: string): string {
  return databaseConnectionPath(databaseId, 'upstream')
}

export function isDatabaseConnectionSubItemActive(
  pathname: string,
  databaseId: string,
  view: DatabaseConnectionView,
): boolean {
  if (!isDatabaseConnectionPath(pathname, databaseId)) return false
  return databaseConnectionViewSegment(pathname, databaseId) === view
}

export function isDatabaseNavItemActive(
  pathname: string,
  _search: string,
  item: DatabaseNavItem,
  flags: DatabaseNavFlags,
): boolean {
  if (
    item.kind === 'general' ||
    item.kind === 'classification' ||
    item.kind === 'snapshots' ||
    item.kind === 'statistics' ||
    item.kind === 'users'
  ) {
    return pathname === item.path || pathname.startsWith(`${item.path}/`)
  }
  const dbMatch = pathname.match(/^\/databases\/(\d+)/)
  if (!dbMatch) return false
  const databaseId = dbMatch[1]
  if (isDatabaseSchemaPath(pathname, databaseId) || isDatabaseConnectionPath(pathname, databaseId)) {
    return false
  }

  const normalizedPath = pathname.replace(/\/$/, '')
  const normalizedItemPath = item.path.replace(/\/$/, '')
  if (normalizedPath === normalizedItemPath) {
    return true
  }

  if (item.kind === 'detailed_rules') {
    const isRoot = normalizedPath === `/databases/${databaseId}`
    return isRoot && defaultDatabaseDetailTab(flags) === 'detailed_rules'
  }

  const segment = databaseDetailPathSegment(pathname, databaseId)
  if (segment && !DATABASE_DETAIL_TAB_ROUTE_SEGMENTS.includes(segment)) {
    return false
  }
  const mode = flags.selfHosted ? 'self_hosted' : 'cloud'
  const tab = parseDatabaseDetailPathSegment(segment, mode, flags)
  return tab === item.kind
}
